import { useEffect, useState } from 'react';
import InputField from '../../../component/form/FormInput';
import Modal from '../../../component/modal/Modal';
import Button from '../../../component/ui/Button';
import '../../../layout/Layout.scss';
import { PageSmallLoader } from '../../../component/PageLoader';

const AddCardModel = ({ isAddCardModal, setIsAddCardModal, layoutSettings, setLayoutSettings }) => {
	const { card_list: cardsList } = layoutSettings;
	const [search, setSearch] = useState('');
	const [cardsState, setCardsState] = useState([]);
	const [originalCards, setOriginalCards] = useState([]);
	const [isLoading, setLoading] = useState(false);
	useEffect(() => {
		if (isAddCardModal) {
			setLoading(true);
			setCardsState(cardsList);
			setOriginalCards(cardsList);
			setLoading(false);
		}
	}, [isAddCardModal, cardsList]);

	const filteredGroupedCards = cardsState.reduce((acc, card) => {
		if (search && !card.title.toLowerCase().includes(search.toLowerCase())) return acc;
		if (!acc[card.category]) acc[card.category] = [];
		acc[card.category].push(card);
		return acc;
	}, {});

	const toggleCard = (id, checked) => {
		const updated = cardsState.map((card) =>
			card.layout_card_id === id ? { ...card, is_checked: checked } : card,
		);
		setCardsState(updated);
	};

	const handleClose = () => {
		setCardsState(originalCards);
		setIsAddCardModal(false);
	};

	const handleSave = () => {
		const activeLayout = layoutSettings?.active_layout?.layout || [];

		// If the card list failed to load, cardsState is empty — and the
		// intersection below would then reduce the layout to []. That is how
		// saved layouts got wiped to an empty array while the /dashboard/cards
		// endpoint was returning zero cards: opening this modal and saving
		// silently deleted every card from the layout. Never let an empty card
		// list destroy an existing layout.
		if (!cardsState.length && activeLayout.length) {
			setIsAddCardModal(false);
			return;
		}

		const checkedCards = cardsState.filter((c) => c.is_checked);
		const checkedIds = checkedCards.map((c) => c.layout_card_id);

		let updatedLayout = activeLayout.filter((item) => checkedIds.includes(item.i));

		const existingIds = updatedLayout.map((item) => item.i);

		const newlyAdded = checkedCards.filter((c) => !existingIds.includes(c.layout_card_id));

		let yMax = 0;
		activeLayout.forEach((item) => {
			if (item.y + item.h > yMax) yMax = item.y + item.h;
		});

		const defaultW = 2;
		const defaultH = 0.7;

		const newLayouts = newlyAdded.map((card, index) => ({
			i: card.layout_card_id,
			x: (index * defaultW) % 10,
			y: yMax,
			w:
				card.type === 'tabs'
					? 3.0
					: card.type === 'table'
						? 4
						: card.type === 'inventory_summary' || card.type === 'top_selling_products'
							? 3.0
							: defaultW,
			h:
				card.type === 'tabs' || card.type === 'table'
					? 2.3
					: card.type === 'inventory_summary' || card.type === 'top_selling_products'
						? 1.15
						: defaultH,
		}));

		const finalLayout = [...updatedLayout, ...newLayouts];

		setLayoutSettings((prev) => ({
			...prev,
			card_list: cardsState,
			active_layout: {
				...prev.active_layout,
				layout: finalLayout,
			},
		}));

		setIsAddCardModal(false);
	};

	return (
		<Modal
			className="addCardModel"
			title={`Customize Cards`}
			secondaryText={`${cardsState.filter((card) => card.is_checked).length} of ${cardsState.length} selected`}
			isOpen={isAddCardModal}
			onClose={handleClose}
			cancelButton={handleClose}
		>
			<div className="d-flex flex-row gap-4 align-items-center py-4">
				<div className="col">
					<InputField
						label=""
						placeholder="Search"
						type="text"
						autoComplete="off"
						required={false}
						autoFocus={false}
						value={search}
						onChange={setSearch}
					/>
				</div>
				<div
					className="lead text-secondary cursor-pointer"
					onClick={() => {
						setSearch('');
					}}
				>
					Reset
				</div>
			</div>

			<div style={{ width: '100%', height: '500px', overflow: 'auto' }}>
				{isLoading ? (
					<div className="d-flex justify-content-center">
						<PageSmallLoader />
					</div>
				) : Object.keys(filteredGroupedCards).length === 0 ? (
					<div className="text-center py-4 text-muted">No cards found.</div>
				) : (
					Object.entries(filteredGroupedCards).map(([category, cardList]) => {
						return (
							<div
								key={category}
								style={{ marginBottom: '20px' }}
							>
								<div
									className="lead"
									style={{ fontWeight: '700', color: '#000' }}
								>
									{category}
								</div>
								<div style={{ marginTop: '10px' }}>
									{cardList.map((card, index) => (
										<div
											key={index}
											style={{
												display: 'flex',
												alignItems: 'center',
												marginBottom: '10px',
												borderRadius: '8px',
												gap: '10px',
												padding: '8px 11px',
												backgroundColor: '#EDEDF7',
											}}
										>
											<input
												type="checkbox"
												checked={card.is_checked}
												onChange={(e) => toggleCard(card.layout_card_id, e.target.checked)}
												style={{ width: '15px', height: '15px', cursor: 'pointer' }}
											/>
											<span className="lead text-secondary">{card.title}</span>
										</div>
									))}
								</div>
							</div>
						);
					})
				)}
			</div>

			<div className="d-flex flex-row align-items-center gap-4 py-4">
				<Button
					type="button"
					button="Save"
					className="mt-0 px-4 btn-primary relative"
					onClick={handleSave}
				/>
				<Button
					type="button"
					button="Cancel"
					className="mt-0 px-4 btn-secondary relative"
					onClick={handleClose}
				/>
			</div>
		</Modal>
	);
};

export default AddCardModel;
